// Vendor Console (CEO 2026-08-13) — one row per vendor, whatever the source.
//
// ── WHY THIS PAGE EXISTS ────────────────────────────────────────────────────
// On 2026-08-13 a production audit found ten suppliers configured with
// credentials and a pricing endpoint, ONE switched on, and 16 of 8,079 products
// carrying a real volume price curve. Three separate failures — Gemline's
// pricing route written and never run, the decoration rate card shipped with no
// data, PCNA's price curve read and discarded — and not one threw an error or
// appeared on any screen. Finding them took a day of hand-written SQL.
//
// This page is that SQL, permanently, on a screen.
//
// ── SOURCE-AGNOSTIC (CEO) ───────────────────────────────────────────────────
// "Whether it's promostandards, directly from PCNA, or right from Lululemon."
// A PromoStandards-shaped view is exactly what kept PCNA's 2,807 products
// invisible — they arrive by bulk file, so a screen keyed on API routes shows
// them as "not configured". Every vendor renders the same way here, and the
// SOURCE is a column rather than an assumption.

import type React from "react";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import type {
  VendorConsoleRow,
  VendorConsoleTotals,
  VendorConsoleState,
  VendorIngestKind,
} from "@merchd/shared";

interface ConsoleResponse {
  rows: VendorConsoleRow[];
  totals: VendorConsoleTotals;
}

const STATE_LABEL: Record<VendorConsoleState, string> = {
  quotable: "Quotable",
  flat_priced: "Flat priced",
  catalogue_only: "Catalogue only",
  configured_not_run: "Not yet run",
  not_started: "Not started",
};

// 🔴 `catalogue_only` is the state this page exists to make impossible to miss:
// products ingested fine, nothing sellable. It looks identical to "working" on
// every other screen in the app.
const STATE_TONE: Record<VendorConsoleState, string> = {
  quotable: "bg-peach-100 text-brown-500",
  flat_priced: "bg-cream-100 text-brown-500",
  catalogue_only: "border border-rust-300 bg-rust-50 text-rust-600",
  configured_not_run: "border border-brown-200 text-brown-400",
  not_started: "border border-brown-100 text-brown-300",
};

const KIND_LABEL: Record<VendorIngestKind, string> = {
  promostandards: "PromoStandards",
  bulk_file: "Bulk file",
  esp: "ESP",
  manual: "Manual",
  none: "—",
};

export default function VendorConsolePage(): React.ReactElement {
  const { user } = useAuth();
  const roles = user?.roles ?? [];
  const isStaff = roles.includes("admin") || roles.includes("production_rep");

  const { data, isLoading, error } = useQuery<ConsoleResponse>({
    queryKey: ["vendor-console"],
    queryFn: () => api("/vendor-console"),
    enabled: isStaff,
  });

  const rows = useMemo(() => data?.rows ?? [], [data]);
  const totals = data?.totals;

  if (!isStaff) {
    return (
      <div className="p-6 text-sm text-brown-400">
        The vendor console is admin-only.
      </div>
    );
  }

  return (
    <div className="space-y-5 p-6">
      <header>
        <h1 className="text-xl font-semibold text-brown-600">Vendor console</h1>
        <p className="mt-1 text-sm text-brown-400">
          Every vendor in the catalogue, however its data reaches us, and
          whether a rep can actually quote it.
        </p>
      </header>

      {isLoading && <p className="text-sm text-brown-300">Loading…</p>}
      {error && (
        <p className="text-sm text-rust-600">
          Could not load the vendor console.
        </p>
      )}

      {totals && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Vendors" value={totals.vendors} />
          <Stat label="Products" value={totals.products} />
          {/* 🔒 These two are DIFFERENT NUMBERS and are shown separately on
              purpose. Collapsing them is how "48% of products are priced" was
              reported when 3,877 carried a single flat bracket and the real
              volume-priced count was 16. */}
          <Stat
            label="With any price"
            value={totals.withAnyBracket}
            sub={`${totals.unquotable.toLocaleString()} with none`}
          />
          <Stat
            label="With a volume curve"
            value={totals.withVolumeCurve}
            tone={totals.withVolumeCurve === 0 ? "alarm" : "normal"}
            sub={
              totals.catalogueOnly > 0
                ? `${totals.catalogueOnly} vendor${totals.catalogueOnly === 1 ? "" : "s"} sell nothing`
                : undefined
            }
          />
        </div>
      )}

      <div className="overflow-x-auto rounded-md border border-brown-100">
        <table className="w-full text-sm">
          <thead className="border-b border-[#e7e7e4] bg-[#fafaf8] text-left text-[11px] font-medium text-muted">
            <tr>
              <th className="px-3 py-2">Vendor</th>
              <th className="px-3 py-2">Source</th>
              <th className="px-3 py-2">State</th>
              <th className="px-3 py-2 text-right">Products</th>
              <th className="px-3 py-2 text-right">Priced</th>
              <th className="px-3 py-2 text-right">Volume curve</th>
              <th className="px-3 py-2 text-right">Supplier retail</th>
              <th className="px-3 py-2">Next action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={`${r.vendorId}-${r.kind}`}
                className="border-t border-brown-50/70 align-top"
              >
                <td className="px-3 py-2 font-semibold text-brown-500">
                  <Link
                    to={`/admin/vendors/${r.vendorId}`}
                    className="hover:text-rust-500 hover:underline"
                  >
                    {r.vendorName}
                  </Link>
                </td>
                <td className="px-3 py-2 text-brown-400">
                  {KIND_LABEL[r.kind]}
                  {r.kind === "promostandards" && !r.pricingDeclared && (
                    // The distinction PCNA proved matters: a declared route and
                    // a route that returns data are different questions.
                    <span className="ml-1 text-[10px] text-rust-500">
                      no pricing route
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <span
                    className={cn(
                      "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                      STATE_TONE[r.state],
                    )}
                  >
                    {STATE_LABEL[r.state]}
                  </span>
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-brown-500">
                  {r.products.toLocaleString()}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-brown-400">
                  {r.withAnyBracket.toLocaleString()}
                </td>
                <td
                  className={cn(
                    "px-3 py-2 text-right tabular-nums",
                    r.withVolumeCurve === 0 && r.products > 0
                      ? "font-semibold text-rust-600"
                      : "text-brown-400",
                  )}
                >
                  {r.withVolumeCurve.toLocaleString()}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-brown-400">
                  {r.withSupplierRetail.toLocaleString()}
                </td>
                <td className="px-3 py-2 text-xs text-brown-400">
                  {r.nextAction ?? (
                    <span className="text-muted">—</span>
                  )}
                </td>
              </tr>
            ))}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-brown-300">
                  No vendors hold products or a declared route yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-brown-300">
        &ldquo;Priced&rdquo; means the product has at least one cost bracket.
        &ldquo;Volume curve&rdquo; means it has more than one — the price
        actually changes with quantity. A product can be priced and still quote
        the same at 5 units as at 5,000.
      </p>
    </div>
  );
}

function Stat(props: {
  label: string;
  value: number;
  sub?: string;
  tone?: "normal" | "alarm";
}): React.ReactElement {
  return (
    <div className="rounded-md border border-brown-100 bg-white px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-brown-300">
        {props.label}
      </p>
      <p
        className={cn(
          "mt-0.5 text-lg font-semibold tabular-nums",
          props.tone === "alarm" ? "text-rust-600" : "text-brown-600",
        )}
      >
        {props.value.toLocaleString()}
      </p>
      {props.sub && (
        <p className="text-[10px] text-brown-300">{props.sub}</p>
      )}
    </div>
  );
}
